import React, {createContext, useCallback, useEffect, useRef} from "react";
import {useLessonPlaybackState} from "../hooks/useLessonPlaybackState";
import useEmitterListener from "../hooks/useEmitterListener";

const AudioPlaybackContext = createContext();

function AudioPlaybackProvider({children, audioSrc, subSectionIndex}) {

    const audioRef = useRef(new Audio(audioSrc));
    const { state, play, pause, finished, reset, on, off } = useLessonPlaybackState();

    // update the source of audioRef to align it to audioSrc
    useEffect(() => {
        if (audioRef.current.src !== audioSrc && audioSrc !== '') {
            audioRef.current.src = audioSrc;
            audioRef.current.load(); // Reload the audio with the new source
        }

        if(audioSrc !== '') {
            play();
        }
    }, [audioSrc, subSectionIndex]); // SubSectionIndex is used to trigger a reload of the audio because some audio files are the same for different subsections

    audioRef.current.onended = useCallback(() => {
        // Log suspiciously short audio durations to help diagnose truncated/wrong
        // audio files reported in content tickets (e.g. ARTFRAN-12273).
        if (audioRef.current.duration > 0 && audioRef.current.duration < 2) {
            console.warn(
                '[AudioPlayback] Audio ended very quickly (' + audioRef.current.duration.toFixed(2) + 's).' +
                ' The audio file may be truncated or pointing to a wrong asset. src=' + audioRef.current.src
            );
        }
        finished();
    }, [finished]);

    const handlePlay = useEmitterListener((player) => {
        audioRef.current.play().catch((error) => {
            console.error('Error playing audio', error);
            reset(); // Emit an event to reset the activity
        });
    }, []);

    const handlePause = useEmitterListener((player) => {
        audioRef.current.pause();
    }, []);

    const handleStop = useEmitterListener((player) => {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
    }, []);

    const cleanup = useCallback(() => {
        off('play', handlePlay);
        off('pause', handlePause);
        off('stop', handleStop);
        audioRef.current.pause();
    }, [off, handlePlay, handlePause, handleStop])

    useEffect(() => {
        console.log(`Registering Audio listeners`)
        on('play', handlePlay, 'audio');
        on('pause', handlePause, 'audio');
        on('stop', handleStop, 'audio');

        return () => {
            cleanup();
        };
    }, []); // Only register once

    const togglePlayback = useCallback(() => {
        if (state === 'playing') {
            pause();
        } else {
            play();
        }
    }, [state, play, pause]);

    const resetAudio = useCallback(() => {
        reset();
        audioRef.current.currentTime = 0;
    }, [reset]);

    const setAudioSrc = useCallback( (newAudioSrc) => {
        audioRef.current.src = newAudioSrc;
        play();
    }, [audioRef.current])

    const values = {
        audioRef,
        state,
        togglePlayback,
        resetAudio,
        setAudioSrc
    };

    return (
        <AudioPlaybackContext.Provider value={values}>
            {children}
        </AudioPlaybackContext.Provider>
    );
}

export {AudioPlaybackProvider, AudioPlaybackContext};